import { useEffect, useMemo, useState } from 'react';
import { shouldRenderBannerPrefetched } from '@atlassian/browser-storage-controls';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useConsentBannerUserPreferencesResource,
	type JiraPrefetchedConsentPreferenceData,
} from '@atlassian/jira-router-resources-cookie-consent/src/index.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { BANNER_NAME } from '../constants.tsx';
import { isInIframe, useCookieConsentBannerSettings } from '../services/preferences/index.tsx';

export const shouldRenderBanner = (data: JiraPrefetchedConsentPreferenceData | null) => {
	if (__SERVER__ && !fg('jfp-magma-ssr-iv-cookie-consent-banner')) {
		return false;
	}
	try {
		if (fg('jfp-magma-ssr-iv-cookie-consent-banner')) {
			/**
			 * If we are using the cookie data to verify if we show the banner, and we don't use ATLASSIAN_SHOW_BANNER_COOKIE_KEY
			 * we end up in a situation where the banner is shown,
			 * then the client renders ConsentBanner from '@atlassian/browser-storage-controls'
			 * which then checks that cookie and if it's false
			 * and then we dismiss the banner anyway
			 *
			 * This is because the cookie consent logic does not set the 'fallback' cookie when we prefetch the data
			 * Because of this, shouldRenderBannerPrefetched will always end up returning true if we prefetch
			 *
			 * If we look at the getShowBanner code platform/packages/policy-platform/browser-storage-controls/src/ui/consent-banner/use-banner-visibility/index.ts
			 * We can see that it will use this show banner cookie to determine if the banner should be shown
			 * as long as we aren't in localConsentMode (which we aren't since we do not pass localConsentMode=true to ConsentBanner
			 * in jira/src/packages/platform/ui/banners/cookie-consent/src/ui/banner/CookieConsentBanner.tsx)
			 *
			 * So we can use the ATLASSIAN_SHOW_BANNER_COOKIE_KEY to determine if the banner should be shown instead if it is set
			 */
			if (data?.shouldShowBanner !== undefined) {
				return data.shouldShowBanner;
			}
		}
		if (fg('fedramp-jira-banner-fix')) {
			return data === null || shouldRenderBannerPrefetched(data);
		}
		return data && shouldRenderBannerPrefetched(data);
	} catch (e) {
		return true;
	}
};

const useBannerCookieConsentOld = () => {
	const [{ isDismissed, height }, { onShow }] = useCookieConsentBannerSettings();

	const { data } = useConsentBannerUserPreferencesResource();

	useEffect(() => {
		const shouldRender = shouldRenderBanner(data);
		// only show banner if banner should be shown and not in iframe
		if (shouldRender && !isInIframe()) {
			// the initial state of banner is "Dismissed" so we need to show it if we "think" it should be shown
			// in case we are making a mistake and CookieBanner will not display
			// it will inform us via `onConsentBannerBailOut` and we will dismiss the banner again.
			onShow();
		}
	}, [data, onShow]);

	return useMemo(
		() => ({
			isEnabled: !isDismissed,
			name: BANNER_NAME,
			height,
		}),
		[isDismissed, height],
	);
};

const useBannerCookieConsentNew = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ isDismissed, height, hasInit }, { onShow, init }] = useCookieConsentBannerSettings();

	const { data, loading, error } = useConsentBannerUserPreferencesResource();

	// This is the initial state of the banner esp in ssr land
	const [initialShouldShow] = useState(() => {
		if (loading) {
			return false;
		}
		if (error) {
			return true;
		}
		return Boolean(shouldRenderBanner(data) && !isInIframe());
	});

	const shouldShow = useMemo(
		() => (hasInit ? !isDismissed : initialShouldShow),
		[hasInit, initialShouldShow, isDismissed],
	);

	useEffect(() => {
		if (hasInit || loading) {
			return;
		}
		// Now that we're in the client render, let's re-verify if we should show the banner
		// Eg to verify if we are in an iframe
		const shouldRender = Boolean(error || (shouldRenderBanner(data) && !isInIframe()));
		init(!shouldRender);

		// We should verify how often we end up having a mismatch between the initial render (probably ssr) and init render (client)
		if (!fg('killswitch-analytics-ssr-cookie-consent-banner')) {
			if (initialShouldShow !== shouldRender) {
				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'cookieConsentBanner showBannerMismatch',
					{
						isInIframe: isInIframe(),
						initialShouldShow,
						initShouldShow: shouldRender,
					},
				);
			}
		}
	}, [createAnalyticsEvent, data, error, hasInit, init, initialShouldShow, loading, onShow]);

	return useMemo(
		() => ({
			isEnabled: shouldShow,
			name: BANNER_NAME,
			height,
		}),
		[shouldShow, height],
	);
};

export const useBannerCookieConsent = functionWithCondition(
	() => fg('jfp-magma-ssr-iv-cookie-consent-banner'),
	useBannerCookieConsentNew,
	useBannerCookieConsentOld,
);
